import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Máquina de estados da edição inline — agnóstica de UI.
 *
 * `error` mantém o rascunho de propósito: perder o que a pessoa digitou porque
 * a rede caiu é pior do que exibir um campo com pendência.
 */
export type InlineEditStatus = "idle" | "editing" | "saving" | "error";

export type UseInlineEditOptions<T> = {
  /** Valor canônico vindo do servidor. */
  value: T;
  onCommit: (next: T) => Promise<void>;
  /** Comparação usada para descartar commits que não mudam nada. */
  isEqual?: (a: T, b: T) => boolean;
  disabled?: boolean;
};

export function useInlineEdit<T>({
  value,
  onCommit,
  isEqual,
  disabled = false,
}: UseInlineEditOptions<T>) {
  const [status, setStatus] = useState<InlineEditStatus>("idle");
  const [draft, setDraft] = useState<T>(value);
  const [error, setError] = useState<string | null>(null);

  // Valor que estava em tela quando a edição começou. Se o servidor atualizar
  // `value` no meio (invalidação de query, outro usuário salvando), o rascunho
  // não pode ser sobrescrito — a pessoa está digitando.
  const editingBaseRef = useRef<T>(value);
  // Enter dispara commit e o blur do desmonte dispararia de novo.
  const committedRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const same = useCallback(
    (a: T, b: T) => (isEqual ? isEqual(a, b) : Object.is(a, b)),
    [isEqual],
  );

  // Fora do modo de edição, o rascunho acompanha o servidor.
  //
  // O update é funcional e comparado: `setDraft(value)` direto entrava em loop
  // infinito quando `value` é um objeto recriado a cada render (o caso do
  // editor de data, que fazia `new Date(...)` no corpo do componente). Como o
  // React só descarta um setState quando o valor é idêntico por Object.is, um
  // objeto novo — ainda que representando a MESMA data — disparava outro
  // render, que criava outro objeto, e assim por diante. Devolver `prev`
  // quando os valores são equivalentes corta o ciclo na raiz, para este e
  // para qualquer editor futuro.
  useEffect(() => {
    if (status !== "idle") return;
    setDraft((prev) => (same(prev, value) ? prev : value));
  }, [value, status, same]);

  const start = useCallback(() => {
    if (disabled || status === "saving") return;
    editingBaseRef.current = value;
    committedRef.current = false;
    setDraft(value);
    setError(null);
    setStatus("editing");
  }, [disabled, status, value]);

  const cancel = useCallback(() => {
    committedRef.current = true;
    setDraft(value);
    setError(null);
    setStatus("idle");
  }, [value]);

  const commit = useCallback(
    async (explicit?: T) => {
      if (committedRef.current && explicit === undefined) return;
      const next = explicit !== undefined ? explicit : draft;
      committedRef.current = true;

      // Nada mudou: volta ao normal sem chamar a mutation. Evita encher a
      // timeline de "atualizado" a cada clique acidental.
      if (same(next, editingBaseRef.current)) {
        setStatus("idle");
        setError(null);
        return;
      }

      setStatus("saving");
      setError(null);
      try {
        await onCommit(next);
        if (!mountedRef.current) return;
        setStatus("idle");
      } catch (err) {
        if (!mountedRef.current) return;
        setDraft(next);
        setError(err instanceof Error ? err.message : String(err));
        setStatus("error");
        committedRef.current = false;
      }
    },
    [draft, onCommit, same],
  );

  /** Props do elemento clicável que entra em modo de edição. */
  const triggerProps = {
    role: "button" as const,
    tabIndex: disabled ? -1 : 0,
    "aria-disabled": disabled || undefined,
    onClick: (e: React.MouseEvent) => {
      if (disabled) return;
      e.stopPropagation();
      start();
    },
    onKeyDown: (e: React.KeyboardEvent) => {
      if (disabled) return;
      if (e.key === "Enter" || e.key === "F2" || e.key === " ") {
        e.preventDefault();
        e.stopPropagation();
        start();
      }
    },
  };

  /** Props do input/textarea em modo de edição. */
  const fieldProps = {
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        cancel();
      } else if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        e.stopPropagation();
        void commit();
      }
    },
    onBlur: () => void commit(),
  };

  return {
    status,
    draft,
    setDraft,
    error,
    start,
    cancel,
    commit,
    triggerProps,
    fieldProps,
    /**
     * Inclui "error" de propósito: o campo precisa continuar aberto com o
     * texto que a pessoa digitou.
     *
     * Só `=== "editing"` desmontava o input assim que a gravação falhava e o
     * rascunho — que o hook guarda justamente para não perder o trabalho —
     * ficava no state, invisível. Digitar um valor inválido e clicar fora
     * apagava o que foi digitado e devolvia o valor antigo, sem explicação.
     */
    isEditing: status === "editing" || status === "error",
    isSaving: status === "saving",
    hasError: status === "error",
  };
}
